/**
 * ConfigurationTab - Dataset configuration editing
 * @module components/starnexus/datasets/tabs/ConfigurationTab
 *
 */

import React, { useState, useEffect } from 'react';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Container from '@amzn/awsui-components-console/container';
import Header from '@amzn/awsui-components-console/header';
import Button from '@amzn/awsui-components-console/button';
import Tabs from '@amzn/awsui-components-console/tabs';
import Alert from '@amzn/awsui-components-console/alert';
import type { Dataset, DatasetConfig } from '../../../../types/starnexus';
import { datasetService } from '../../../../services/starnexus';
import { BasicSettingsForm } from '../../wizard/forms/BasicSettingsForm';
import { SchemaConfigForm } from '../../wizard/forms/SchemaConfigForm';
import { SqlEditorForm } from '../../wizard/forms/SqlEditorForm';
import { ProcessingOptionsForm } from '../../wizard/forms/ProcessingOptionsForm';
import { AndesSettingsForm } from '../../wizard/forms/AndesSettingsForm';

interface ConfigurationTabProps {
  dataset: Dataset;
  onUpdate: (dataset: Dataset) => void;
}

export const ConfigurationTab: React.FC<ConfigurationTabProps> = ({
  dataset,
  onUpdate
}) => {
  const [config, setConfig] = useState<DatasetConfig>(dataset.config);
  const [originalConfig] = useState<DatasetConfig>(dataset.config);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    const changed = JSON.stringify(config) !== JSON.stringify(originalConfig);
    setHasChanges(changed);
  }, [config, originalConfig]);

  const handleSave = async () => {
    setSaving(true);
    setSaveSuccess(false);
    setSaveError(null);

    try {
      const updated = await datasetService.updateDataset(
        dataset.datasetId,
        config
      );
      if (updated) {
        onUpdate(updated);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        setSaveError('Failed to save changes');
      }
    } catch (err) {
      setSaveError('An error occurred while saving');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setConfig(originalConfig);
    setSaveSuccess(false);
    setSaveError(null);
  };

  return (
    <SpaceBetween size="m">
      {saveSuccess && (
        <Alert
          type="success"
          dismissible
          onDismiss={() => setSaveSuccess(false)}
        >
          Configuration saved successfully.
        </Alert>
      )}
      {saveError && (
        <Alert type="error" dismissible onDismiss={() => setSaveError(null)}>
          {saveError}
        </Alert>
      )}

      <Container
        header={
          <Header
            variant="h2"
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button disabled={!hasChanges} onClick={handleReset}>
                  Reset
                </Button>
                <Button
                  variant="primary"
                  disabled={!hasChanges}
                  loading={saving}
                  onClick={handleSave}
                >
                  Save changes
                </Button>
              </SpaceBetween>
            }
          >
            Dataset Configuration
          </Header>
        }
      >
        <Tabs
          tabs={[
            {
              id: 'basic',
              label: 'Basic Settings',
              content: (
                <div style={{ padding: '16px 0' }}>
                  <BasicSettingsForm
                    settings={config.basicSettings}
                    onChange={(basicSettings) =>
                      setConfig({ ...config, basicSettings })
                    }
                  />
                </div>
              )
            },
            {
              id: 'schema',
              label: 'Schema',
              content: (
                <div style={{ padding: '16px 0' }}>
                  <SchemaConfigForm
                    schema={config.schemaConfig}
                    onChange={(schemaConfig) =>
                      setConfig({ ...config, schemaConfig })
                    }
                    onRegenerate={() => {}}
                  />
                </div>
              )
            },
            {
              id: 'sql',
              label: 'SQL Transform',
              content: (
                <div style={{ padding: '16px 0' }}>
                  <SqlEditorForm
                    sql={config.sqlTransform}
                    onChange={(sqlTransform) =>
                      setConfig({ ...config, sqlTransform })
                    }
                    onRegenerate={() => {}}
                  />
                </div>
              )
            },
            {
              id: 'processing',
              label: 'Processing Options',
              content: (
                <div style={{ padding: '16px 0' }}>
                  <ProcessingOptionsForm
                    options={config.processingOptions}
                    onChange={(processingOptions) =>
                      setConfig({ ...config, processingOptions })
                    }
                  />
                </div>
              )
            },
            {
              id: 'andes',
              label: 'Andes Settings',
              content: (
                <div style={{ padding: '16px 0' }}>
                  <AndesSettingsForm
                    settings={config.andesSettings}
                    onChange={(andesSettings) =>
                      setConfig({ ...config, andesSettings })
                    }
                  />
                </div>
              )
            }
          ]}
        />
      </Container>
    </SpaceBetween>
  );
};

export default ConfigurationTab;
